import React, { useState } from 'react';
import { Modal, Button, Typography, Spin, Alert } from 'antd';
import { X, Download, Maximize } from 'lucide-react';
import Icon from './Icon';
import { getFileType } from '../services/filesService';

const { Text } = Typography;

const FileViewer = ({ open, onClose, file }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  if (!file) return null;

  const fileType = getFileType(file.name);

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError('Failed to load file');
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    window.open(file.url, '_blank');
  };

  const renderFileContent = () => {
    switch (fileType) {
      case 'pdf':
        return (
          <div style={{ width: '100%', height: '70vh', position: 'relative' }}>
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <Spin size="large" />
              </div>
            )}
            <iframe
              src={file.url}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              onLoad={handleLoad}
              onError={handleError}
              title={file.name}
            />
          </div>
        );

      case 'image':
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              maxHeight: '70vh',
              overflow: 'auto',
            }}
          >
            {loading && <Spin size="large" />}
            <img
              src={file.url}
              alt={file.name}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                display: loading ? 'none' : 'block',
              }}
              onLoad={handleLoad}
              onError={handleError}
            />
          </div>
        );

      case 'video':
        return (
          <div style={{ width: '100%', maxHeight: '70vh' }}>
            {loading && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '32px 0',
                }}
              >
                <Spin size="large" />
              </div>
            )}
            <video
              controls
              width="100%"
              style={{
                maxHeight: '70vh',
                display: loading ? 'none' : 'block',
              }}
              onLoadedData={handleLoad}
              onError={handleError}
            >
              <source src={file.url} />
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case 'audio':
        return (
          <div style={{ width: '100%', padding: '32px 0' }}>
            {loading && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '32px 0',
                }}
              >
                <Spin size="large" />
              </div>
            )}
            <audio
              controls
              style={{
                width: '100%',
                display: loading ? 'none' : 'block',
              }}
              onLoadedData={handleLoad}
              onError={handleError}
            >
              <source src={file.url} />
              Your browser does not support the audio tag.
            </audio>
          </div>
        );

      default:
        return (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Preview not available
            </Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              This file type cannot be previewed in the browser.
            </Text>
            <Button
              type="primary"
              icon={<Icon as={Download} />}
              onClick={handleDownload}
            >
              Download to view
            </Button>
          </div>
        );
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingRight: 40,
          }}
        >
          <Text
            className="airsmart-dialog-file-name"
            ellipsis
            style={{ flex: 1, marginRight: 16, fontSize: 16 }}
          >
            {file.name}
          </Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="text"
              shape="circle"
              icon={<Icon as={Maximize} />}
              onClick={handleOpenInNewTab}
              title="Open in new tab"
            />
            <Button
              type="text"
              shape="circle"
              icon={<Icon as={Download} />}
              onClick={handleDownload}
              title="Download"
            />
          </div>
        </div>
      }
      className="airsmart-dialog"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Close</Button>
          <Button icon={<Icon as={Download} />} onClick={handleDownload}>
            Download
          </Button>
        </div>
      }
      width={1024}
      closeIcon={<Icon as={X} />}
      styles={{
        body: { padding: 0 },
        content: { height: '90vh' },
      }}
      destroyOnClose
    >
      {error ? (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ margin: 16 }}
        />
      ) : (
        renderFileContent()
      )}
    </Modal>
  );
};

export default FileViewer;
